import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Banner } from "@perlab/ui";
import { queryClient } from "../../lib/queryClient";
import { getLatestWindowsGleanRelease } from "../api/gleanReleases";
import { detectGleanPlatform } from "./gleanPlatform";

const RELEASE_CHECK_MS = 5 * 60_000;

function savedDismissal(key: string): string | null {
  try { return window.localStorage.getItem(key)?.slice(0, 200) ?? null; } catch { return null; }
}

/** Availability notice only: a browser download cannot prove an installed version. */
export function GleanReleaseBanner({ actorId, tenant = "" }: { actorId: string | null; tenant?: string }) {
  const key = `velocity.glean.release-dismissed:${encodeURIComponent(tenant)}:${encodeURIComponent(actorId ?? "")}`;
  const [visible, setVisible] = useState(document.visibilityState !== "hidden");
  const [dismissal, setDismissal] = useState(() => ({ key, value: savedDismissal(key) }));
  const enabled = !!actorId && detectGleanPlatform() === "windows" && visible;
  const query = useQuery({
    queryKey: ["glean-helper-release", tenant, actorId],
    queryFn: ({ signal }) => getLatestWindowsGleanRelease(signal),
    enabled, staleTime: RELEASE_CHECK_MS, gcTime: RELEASE_CHECK_MS,
    refetchInterval: RELEASE_CHECK_MS, refetchIntervalInBackground: false,
    refetchOnWindowFocus: true, retry: false,
  }, queryClient);

  useEffect(() => {
    const updateVisibility = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", updateVisibility);
    return () => document.removeEventListener("visibilitychange", updateVisibility);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const check = () => {
      if (document.visibilityState !== "hidden") {
        void queryClient.fetchQuery({
          queryKey: ["glean-helper-release", tenant, actorId],
          queryFn: ({ signal }) => getLatestWindowsGleanRelease(signal),
          staleTime: RELEASE_CHECK_MS, retry: false,
        }).catch(() => { /* An optional release notice must not interrupt an active conversation. */ });
      }
    };
    window.addEventListener("focus", check);
    return () => window.removeEventListener("focus", check);
  }, [enabled, tenant, actorId]);

  useEffect(() => {
    setDismissal({ key, value: savedDismissal(key) });
    const sync = (event: StorageEvent) => {
      if (event.key === key || event.key === null) setDismissal({ key, value: savedDismissal(key) });
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [key]);

  const release = query.data;
  const identity = release ? `${release.version}:${release.sha256}` : "";
  const dismissed = dismissal.key === key ? dismissal.value : savedDismissal(key);
  if (!enabled || query.isError || !release || dismissed === identity) return null;

  function dismiss() {
    setDismissal({ key, value: identity });
    try { window.localStorage.setItem(key, identity); } catch { /* Session memory still dismisses the notice. */ }
  }

  return (
    <div data-app="glean-release-banner">
      <Banner tone="brand" action={<div className="flex flex-wrap items-center gap-3">
        <a href={release.download_url} download="VelocityGleanSetup.exe" className="rounded border px-3 py-1.5 text-sm font-medium" onClick={dismiss}>Install</a>
        <button type="button" onClick={dismiss} className="rounded px-3 py-1.5 text-sm">Not now</button>
      </div>}>
        <strong>Glean Helper {release.version} is available.</strong> Download and install the update?
        <span className="block text-xs">Install downloads the Windows installer. Run it yourself to complete installation.</span>
      </Banner>
    </div>
  );
}
